import { MapTo } from '@adobe/aem-react-editable-components';
import { Component } from 'react';
import { isAEMAuthor, isMobileApp, isInternal, getApiKeyFIS } from '../../utils/api';
import fisConfig from '../../utils/url-directory';

class XFScriptsFragment extends Component {
  loadScript = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${src}"]`);

      if (existingScript) {
        console.log('[XFScriptsFragment] Script already exists:', src);
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = false;

      script.onload = () => {
        console.log('[XFScriptsFragment] Loaded script:', src);
        resolve();
      };

      script.onerror = error => {
        console.error('[XFScriptsFragment] Failed to load script:', src, error);
        reject(error);
      };

      document.body.appendChild(script);
    });
  };

  loadCss = (href: string): void => {
    const existingLink = document.querySelector(`link[href="${href}"]`);

    if (existingLink) {
      console.log('[XFScriptsFragment] CSS already exists:', href);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;

    document.head.appendChild(link);
    console.log('[XFScriptsFragment] Added CSS:', href);
  };

  waitForGlobal = (name: string, timeout = 8000): Promise<any> => {
    return new Promise((resolve, reject) => {
      const started = Date.now();

      const check = () => {
        const value = (window as any)[name];

        if (value) {
          console.log(`[XFScriptsFragment] ${name} is available:`, value);
          resolve(value);
          return;
        }

        if (Date.now() - started > timeout) {
          reject(new Error(`${name} was not available after ${timeout}ms`));
          return;
        }

        setTimeout(check, 100);
      };

      check();
    });
  };

  loadHeaderDependencies = async (): Promise<void> => {
    const w = window as any;

    if (w.__workplaceProfileHeaderDependenciesLoaded) {
      console.log('[XFScriptsFragment] Header dependencies already loaded');
      return;
    }

    if (w.__workplaceProfileHeaderDependenciesLoading) {
      console.log('[XFScriptsFragment] Header dependencies already loading');
      return w.__workplaceProfileHeaderDependenciesLoading;
    }

    console.log('[XFScriptsFragment] Loading header dependencies dynamically');

    w.__workplaceProfileHeaderDependenciesLoading = (async () => {
      this.loadCss('/etc.clientlibs/workplace-fdi/clientlibs/clientlib-site.css');

      /**
       * Order matters:
       * 1. jQuery first because FDI/header scripts use $.
       * 2. clientlib-dependencies and bootstrap next.
       * 3. universal-variables and global-header before clientlib-site.
       * 4. clientlib-site and remaining FDI/header scripts.
       * 5. MegaMenu last because it depends on jQuery and Services.
       */
      await this.loadScript('/workplace/en/participant/profile/jquery-3.3.1.min.js');

      if (!w.$ && w.jQuery) {
        w.$ = w.jQuery;
      }

      console.log('[XFScriptsFragment] jQuery after load:', w.jQuery);
      console.log('[XFScriptsFragment] $ after load:', w.$);

      await this.loadScript('/etc.clientlibs/workplace-fdi/clientlibs/clientlib-dependencies.js');
      await this.loadScript('/etc.clientlibs/workplace-fdi/clientlibs/vendor/bootstrap.js');

      await this.loadScript('/etc.clientlibs/workplace-fdi/components/content/universal-variables/clientlibs.js');
      await this.loadScript('/etc.clientlibs/workplace-fdi/components/content/global-header/clientlibs.js');

      try {
        await this.waitForGlobal('Services', 8000);
      } catch (error) {
        console.warn('[XFScriptsFragment] Services was not available before clientlib-site:', error);
      }

      const remainingScripts = [
        '/etc.clientlibs/workplace-fdi/clientlibs/clientlib-site.js',
        '/etc.clientlibs/workplace-fdi/clientlibs/vendor/underscore-1.9.1.js',
        '/etc.clientlibs/workplace-fdi/clientlibs/clientlib-underscore.js',
        '/etc.clientlibs/workplace-fdi/clientlibs/vendor/moment.js',
        '/etc.clientlibs/workplace-fdi/components/content/sitebar/clientlibs.js',
        '/etc.clientlibs/workplace-fdi/components/content/top-nav/clientlibs.js',
        '/etc.clientlibs/workplace-fdi/components/content/mmr-message/clientlibs.js',
        '/etc.clientlibs/workplace-sites/components/inbox-popup/clientlibs.js',
        '/etc.clientlibs/workplace-sites/components/profile-popup/clientlibs.js',
        '/etc.clientlibs/workplace-fdi/components/content/content-message-notification/clientlibs.js',
        '/etc.clientlibs/workplace-sites/components/foundation/structure/header/v2/rps-mega-menu-bar-container/clientlibs.js'
      ];

      for (const src of remainingScripts) {
        await this.loadScript(src);
      }

      try {
        await this.waitForGlobal('Services', 8000);
      } catch (error) {
        console.warn('[XFScriptsFragment] Services was not ready after dependency load:', error);
      }

      try {
        await this.waitForGlobal('WorkplaceCommon', 8000);
      } catch (error) {
        console.warn('[XFScriptsFragment] WorkplaceCommon was not ready after dependency load:', error);
      }

      console.log('[XFScriptsFragment] Header dependency globals after load:', {
        jQuery: w.jQuery,
        $: w.$,
        Services: w.Services,
        WorkplaceCommon: w.WorkplaceCommon,
        GlobalNav: w.GlobalNav,
        SiteBar: w.SiteBar,
        TopNav: w.TopNav,
        MMRMessage: w.MMRMessage,
        InboxPopup: w.InboxPopup,
        ProfilePopup: w.ProfilePopup,
        initMegaMenu: w.initMegaMenu
      });

      w.__workplaceProfileHeaderDependenciesLoaded = true;
      console.log('[XFScriptsFragment] Header dependencies loaded dynamically');
    })();

    return w.__workplaceProfileHeaderDependenciesLoading;
  };

  safelyInitialize = (name: string, objectRef: any) => {
    console.log(`[XFScriptsFragment][DEBUG] ${name}:`, objectRef);
    console.log(
      `[XFScriptsFragment][DEBUG] ${name}.initialize type:`,
      objectRef && typeof objectRef.initialize
    );
    if (objectRef && typeof objectRef.initialize === 'function') {
      console.log(`[XFScriptsFragment] Initializing ${name}`);
      objectRef.initialize();
    } else {
      console.warn(`[XFScriptsFragment] ${name}.initialize is not available. Skipping.`);
    }
  };

  safelyCall = (name: string, fn: any) => {
    console.log(`[XFScriptsFragment][DEBUG] ${name} type:`, typeof fn);
    if (typeof fn === 'function') {
      console.log(`[XFScriptsFragment] Calling ${name}`);
      fn();
    } else {
      console.warn(`[XFScriptsFragment] ${name} is not available. Skipping.`);
    }
  };

  async componentDidMount() {
    const w = window as any;
    console.log('[XFScriptsFragment] DYNAMIC_HEADER_LOADER_BUILD_2026_08_17_v3');
    console.log('[XFScriptsFragment] componentDidMount started');
    console.log('[XFScriptsFragment] Current URL:', window.location.href);
    console.log('[XFScriptsFragment] Hostname:', window.location.hostname);
    console.log('[XFScriptsFragment] isAEMAuthor:', isAEMAuthor());
    console.log('[XFScriptsFragment] isMobileApp:', isMobileApp());
    console.log('[XFScriptsFragment] isInternal:', isInternal());

    let fisExtApiHost = fisConfig.PROD.EXTERNAL.fisapi;
    let fisIntApiHost = fisConfig.PROD.INTERNAL.fisapi;
    let awsEnvironmentDomain: string;

    if (
      window.location.hostname.indexOf('awstrp.net') > -1 ||
      window.location.hostname.indexOf('fisretirement.net') > -1
    ) {
      if (
        window.location.hostname.indexOf('rpsdev') > -1 ||
        window.location.hostname.indexOf('dev') > -1 ||
        window.location.hostname.indexOf('localhost') > -1
      ) {
        fisIntApiHost = fisConfig.DEV.INTERNAL.fisapi;
      } else if (
        window.location.hostname.indexOf('sit') > -1 ||
        window.location.hostname.indexOf('stage') > -1 ||
        window.location.hostname.indexOf('rpsstg') > -1
      ) {
        fisIntApiHost = fisConfig.STAGE.INTERNAL.fisapi;
      }

      awsEnvironmentDomain = fisIntApiHost;
    } else {
      if (
        window.location.hostname.indexOf('rpsdev') > -1 ||
        window.location.hostname.indexOf('dev') > -1 ||
        window.location.hostname.indexOf('localhost') > -1
      ) {
        fisExtApiHost = fisConfig.DEV.EXTERNAL.fisapi;
      } else if (
        window.location.hostname.indexOf('sit') > -1 ||
        window.location.hostname.indexOf('stage') > -1 ||
        window.location.hostname.indexOf('rpsstg') > -1
      ) {
        fisExtApiHost = fisConfig.STAGE.EXTERNAL.fisapi;
      }

      awsEnvironmentDomain = fisExtApiHost;
    }

    console.log('[XFScriptsFragment] awsEnvironmentDomain:', awsEnvironmentDomain);

    if (!isAEMAuthor() && !isMobileApp()) {
      console.log('[XFScriptsFragment] Initializing XF scripts');

      console.log('[XFScriptsFragment][DEBUG] Available globals before dependency load:', {
        GlobalNav: w.GlobalNav,
        SiteBar: w.SiteBar,
        TopNav: w.TopNav,
        MMRMessage: w.MMRMessage,
        InboxPopup: w.InboxPopup,
        ProfilePopup: w.ProfilePopup,
        WorkplaceCommon: w.WorkplaceCommon,
        Services: w.Services,
        initMegaMenu: w.initMegaMenu,
        updateProspectusLink: w.updateProspectusLink,
        runAuthentication: w.runAuthentication
      });

      const isEnglishStaticProfile =
        window.location.pathname.startsWith('/workplace/en/participant/profile');

      const globalsMissing =
        !w.GlobalNav ||
        !w.SiteBar ||
        !w.TopNav ||
        !w.MMRMessage ||
        !w.InboxPopup ||
        !w.ProfilePopup ||
        !w.WorkplaceCommon ||
        !w.Services ||
        !w.initMegaMenu;

      if (!isEnglishStaticProfile && globalsMissing) {
        try {
          console.log(
            '[XFScriptsFragment] Non-English profile page with missing globals. Loading header dependencies.'
          );
          await this.loadHeaderDependencies();
        } catch (error) {
          console.error('[XFScriptsFragment] Failed to load header dependencies:', error);
        }
      } else {
        console.log('[XFScriptsFragment] Skipping dynamic header dependency load.', {
          isEnglishStaticProfile,
          globalsMissing
        });
      }

      console.log('[XFScriptsFragment][DEBUG] Available globals after dependency load:', {
        GlobalNav: w.GlobalNav,
        SiteBar: w.SiteBar,
        TopNav: w.TopNav,
        MMRMessage: w.MMRMessage,
        InboxPopup: w.InboxPopup,
        ProfilePopup: w.ProfilePopup,
        WorkplaceCommon: w.WorkplaceCommon,
        Services: w.Services,
        initMegaMenu: w.initMegaMenu,
        updateProspectusLink: w.updateProspectusLink,
        runAuthentication: w.runAuthentication
      });
      this.safelyInitialize('GlobalNav', w.GlobalNav);
      this.safelyInitialize('SiteBar', w.SiteBar);
      this.safelyInitialize('TopNav', w.TopNav);
      this.safelyInitialize('MMRMessage', w.MMRMessage);
      this.safelyInitialize('InboxPopup', w.InboxPopup);
      this.safelyInitialize('ProfilePopup', w.ProfilePopup);

      this.safelyCall('updateProspectusLink', w.updateProspectusLink);

      if (isInternal()) {
        this.safelyCall('runAuthentication', w.runAuthentication);
      }

      if (w.WorkplaceCommon && typeof w.WorkplaceCommon.makeServiceCall === 'function') {
        const apiKeyFIS = getApiKeyFIS();
        console.log('[XFScriptsFragment] Calling footer navigation service');
        console.log('[XFScriptsFragment] apiKeyFIS exists:', Boolean(apiKeyFIS));
        w.WorkplaceCommon.makeServiceCall({
          url: awsEnvironmentDomain + '/rps/workplace/navigation/footer',
          type: 'GET',
          headers: {
            Accept: '*/*',
            'X-TRP-Requested-With': 'XMLHttpRequest',
            'trp-request-consumer-initial': 'workplace-profile',
            apiKey: apiKeyFIS || ''
          }
        }).then(function(data: any) {
          console.log('[XFScriptsFragment] Footer navigation response:', data);
          if (data.err) {
            console.warn('[XFScriptsFragment] Footer navigation returned err:', data);
            return;
          }

          if (data.hasTradelinkAccount === false) {
            document.querySelector('#trade-link-legal')?.classList.add('hide');
            document.querySelector('#trade-link-legal div')?.classList.add('hide');
          }

          if (data.hasSelfDirectedAccount === false) {
            document.querySelector('#self-directed-account')?.classList.add('hide');
            document.querySelector('#self-directed-account div')?.classList.add('hide');
          }
        });
      } else {
        console.warn(
          '[XFScriptsFragment] WorkplaceCommon.makeServiceCall is not available. Skipping footer navigation call.'
        );
      }

      if (w.TopNav && typeof w.TopNav.addNavigationEventListeners === 'function') {
        console.log('[XFScriptsFragment] Adding TopNav navigation event listeners');
        w.TopNav.addNavigationEventListeners();
      } else {
        console.warn(
          '[XFScriptsFragment] TopNav.addNavigationEventListeners is not available. Skipping.'
        );
      }

      if (typeof w.initMegaMenu === 'function' && w.Services) {
        try {
          console.log('[XFScriptsFragment] Calling initMegaMenu after Services is ready');
          await w.initMegaMenu();
          console.log('[XFScriptsFragment] initMegaMenu completed');
        } catch (error) {
          console.error('[XFScriptsFragment] initMegaMenu failed:', error);
        }
      } else {
        console.warn('[XFScriptsFragment] Cannot call initMegaMenu.', {
          initMegaMenu: typeof w.initMegaMenu,
          Services: w.Services
        });
      }
    }
  }

  render() {
    return null;
  }
}

export default MapTo('workplace-profile/components/xfscriptscontainer')(XFScriptsFragment);
